import type { LocaleType } from './Locale';

/*
 * Icelandic date format
 * Format: dd.mm.yyyy
 * Short date: d. mmmm yyyy (mmmm, abbreviated month name, starts with a lower case)
 * Long date: d. mmmm yyyy (mmmm starts with a lower case)
 * Weekdays start with a lower case letter unless it starts a sentance or is standalone
 * Time is in 24h format
 *
 * English date format
 * Format: dd/mm/yyyy
 * Short date: d mmmmm yyyy (mmmm abbreviated month name, starts with a capital case)
 * Long date: d mmmmm yyyy (mmmm starts with a capital case)
 * Weekdays always start with a capital letter
 * Time is in 12h format with AM and PM
 *
 * Polish date format
 * Format: dd.mm.yyyy
 * Short date: d mmmm yyyy (mmmmm, abbreviated month name, starts with a lower case)
 * Long date: d mmmm yyyy (mmmm starts with a lower case)
 * Weekdays start with a lower case letter unless it starts a sentance or is standalone
 * Time is in 24h format
 */
/**
 * @ignore
 */
const monthNamesSettings = {
  is: {
    short: [
      'Jan.',
      'Feb.',
      'Mars',
      'Apríl',
      'Maí',
      'Júní',
      'Júlí',
      'Ágúst',
      'Sept.',
      'Okt.',
      'Nóv.',
      'Des.',
    ],
    long: [
      'Janúar',
      'Febrúar',
      'Mars',
      'Apríl',
      'Maí',
      'Júní',
      'Júlí',
      'Ágúst',
      'September',
      'Október',
      'Nóvember',
      'Desember',
    ],
    weekdays: [
      'Sunnudagur',
      'Mánudagur',
      'Þriðjudagur',
      'Miðvikudagur',
      'Fimmtudagur',
      'Föstudagur',
      'Laugardagur',
    ],
  },
  en: {
    short: [
      'Jan.',
      'Feb.',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'Sept.',
      'Oct.',
      'Nov.',
      'Dec.',
    ],
    long: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    weekdays: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
  },
  pl: {
    // ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
    short: [
      'Stycz.',
      'Luty',
      'Mar.',
      'Kwiec.',
      'Maj',
      'Czerw.',
      'Lip.',
      'Sierp.',
      'Wrzes.',
      'Pazdz.',
      'Listop.',
      'Grudz.',
    ],
    long: [
      'Styczeń',
      'Luty',
      'Marzec',
      'Kwiecień',
      'Maj',
      'Czerwiec',
      'Lipiec',
      'Sierpień',
      'Wrzesień',
      'Październik',
      'Listopad',
      'Grudzień',
    ],
    weekdays: [
      'Niedziela',
      'Poniedziałek',
      'Wtorek',
      'Środa',
      'Czwartek',
      'Piątek',
      'Sobota',
    ],
  },
};

/**
 * Casing for month names in Icelandic and Polish are by default lower case but in English the first
 * letter is always capital case.
 * @param {string} monthOrWeekday - The name of the month or weekday
 * @param {LocaleType} locale - A string representing the locale.
 * @param {bool} firstLetterCapital - true if the first letter should be capitalized, default is false.
 */
const monthWeekdayCasing = (
  monthOrWeekday: string,
  locale: LocaleType,
  firstLetterCapital?: boolean,
): string => {
  const toLowerCase =
    locale !== 'en' && (!firstLetterCapital || firstLetterCapital !== true);
  return toLowerCase ? monthOrWeekday.toLowerCase() : monthOrWeekday;
};

/**
 * Format a date value
 * 'is': dd.mm.yyyy
 * 'pl': dd.mm.yyyy
 * 'en': dd/mm/yyyy
 * @param {LocaleType} locale - A string representing the locale.
 * @param {Object} date - A date object representing the value to format.
 * @return {string} String that contains the formatted date value
 */
export const formatDate = (locale: LocaleType, date: Date): string => {
  const day = `0${date.getDate()}`.slice(-2);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();

  switch (locale) {
    case 'en':
      return `${day}/${month}/${year}`;
    case 'pl':
    default:
      // 'pl' 'is', default is 'is'
      return `${day}.${month}.${year}`;
  }
};

/**
 * Format a time value
 * 'is': 24h format
 * 'pl': 24h format
 * 'en': 12h format with AM or PM
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'.
 * @param {Object} time - A date object representing the time value to format.
 * @return {string} String that contains the formatted time value
 */
export const formatTime = (locale: LocaleType, time: Date): string => {
  let mins = `${time.getMinutes()}`;
  if (mins.length === 1) {
    mins = `0${mins}`;
  }

  switch (locale) {
    case 'en':
      return time.getHours() / 12 > 1
        ? `${time.getHours() - 12}:${mins} PM`
        : `${time.getHours()}:${mins} AM`;
    case 'pl':
    default:
      return `${time.getHours()}:${mins}`;
  }
};

/**
 * Return the weekday name
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'
 * @param {Object} date - A date object representing the date value.
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} Name of the week day.
 */
export const formatWeekday = (
  locale: LocaleType,
  date: Date,
  firstLetterCapital?: boolean,
): string =>
  monthWeekdayCasing(
    monthNamesSettings[locale].weekdays[date.getDay()],
    locale,
    firstLetterCapital,
  );

/**
 * Format a date and time value
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'.
 * @param {Object} datetime - A date object representing the date and time value to format.
 * @return {string} String on the form 'DD MM YYYY HH:MM'
 */
export const formatDateTime = (locale: LocaleType, datetime: Date): string =>
  `${formatDate(locale, datetime)} ${formatTime(locale, datetime)}`;

/**
 * Abbreviated month name
 * @param {LocaleType} locale - A string representing the locale.
 * @param {number} idx - Month index, note that it starts at 0
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} String that contains the month name
 */
export const formatShortMonthFromIdx = (
  locale: LocaleType,
  idx: number,
  firstLetterCapital?: boolean,
): string =>
  monthWeekdayCasing(
    monthNamesSettings[locale].short[idx],
    locale,
    firstLetterCapital,
  );

/**
 * Month name
 * @param {LocaleType} locale - A string representing the locale.
 * @param {number} idx - Month index, note that it starts at 0
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} String that contains the month name
 */
export const formatLongMonthFromIdx = (
  locale: LocaleType,
  idx: number,
  firstLetterCapital?: boolean,
): string =>
  monthWeekdayCasing(
    monthNamesSettings[locale].long[idx],
    locale,
    firstLetterCapital,
  );

/**
 * Abbreviated month name
 * @param {LocaleType} locale - A string representing the locale.
 * @param {Object} date - A date object representing the date value.
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} String that contains the month name
 */
export const formatShortMonth = (
  locale: LocaleType,
  date: Date,
  firstLetterCapital?: boolean,
): string =>
  formatShortMonthFromIdx(locale, date.getMonth(), firstLetterCapital);

/**
 * Month name
 * @param {LocaleType} locale - A string representing the locale.
 * @param {Object} date - A date object representing the date value.
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} String that contains the month name
 */
export const formatLongMonth = (
  locale: LocaleType,
  date: Date,
  firstLetterCapital?: boolean,
): string =>
  formatLongMonthFromIdx(locale, date.getMonth(), firstLetterCapital);

/**
 * Return abbreviated month name and the year of the date value.
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'
 * @param {Object} date - A date object representing the date value.
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} Month and year.
 */
export const formatMonthYear = (
  locale: LocaleType,
  date: Date,
  firstLetterCapital?: boolean,
): string =>
  `${formatShortMonth(locale, date, firstLetterCapital)} ${date.getFullYear()}`;

/**
 * Return full month name and the year of the date value.
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'
 * @param {Object} date - A date object representing the date value.
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} Month and year.
 */
export const formatMonthYearLong = (
  locale: LocaleType,
  date: Date,
  firstLetterCapital?: boolean,
): string =>
  `${formatLongMonth(locale, date, firstLetterCapital)} ${date.getFullYear()}`;

/**
 * Return day and the month of the date value.
 * 'is': d. mmmm
 * 'pl': d mmmm
 * 'en': d mmmm
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'
 * @param {Object} date - A date object representing the date value.
 * @param {bool} firstLetterCapital - true if first letter should always be capital, default is false
 * @return {string} Day and month.
 */
export const formatDayMonthLong = (locale: LocaleType, date: Date): string => {
  const month = formatLongMonth(locale, date, false);

  switch (locale) {
    case 'en':
    case 'pl':
      return `${date.getDate()} ${month}`;
    default:
      // is', default is 'is'
      return `${date.getDate()}. ${month}`;
  }
};

/**
 * Format a date value
 * 'is': d. mmmm yyyy
 * 'pl': d mmmm yyyy
 * 'en': d mmmm yyyy
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'.
 * @param {Object} date - A date object representing the value to format.
 * @return {string} String that contains the formatted date value
 */
export const formatDateLong = (locale: LocaleType, date: Date): string => {
  const month = formatLongMonth(locale, date, false);

  switch (locale) {
    case 'en':
    case 'pl':
      return `${date.getDate()} ${month} ${date.getFullYear()}`;
    default:
      // is', default is 'is'
      return `${date.getDate()}. ${month} ${date.getFullYear()}`;
  }
};

/**
 * Format a date value with abbreviated month name
 * 'is': d. mmmm yyyy
 * 'pl': d mmmm yyyy
 * 'en': d mmmm yyyy
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'
 * @param {Object} date - A date object representing the value to format.
 * @return {string} String that contains the formatted date value
 */
export const formatDateShort = (locale: LocaleType, date: Date): string => {
  const month = formatShortMonth(locale, date, false);

  switch (locale) {
    case 'en':
    case 'pl':
      return `${date.getDate()} ${month} ${date.getFullYear()}`;
    default:
      // is', default is 'is'
      return `${date.getDate()}. ${month} ${date.getFullYear()}`;
  }
};

/**
 * Return day and the abbreviated month name of the date value.
 * 'is': d. mmmm
 * 'pl': d mmmm
 * 'en': d mmmm
 * @param {LocaleType} locale - A string representing the locale, eg. 'en'
 * @param {Object} date - A date object representing the date value.
 * @return {string} Day and month.
 */
export const formatDayMonth = (locale: LocaleType, date: Date): string => {
  const month = formatShortMonth(locale, date, false);

  switch (locale) {
    case 'en':
    case 'pl':
      return `${date.getDate()} ${month}`;
    default:
      // is', default is 'is'
      return `${date.getDate()}. ${month}`;
  }
};
